import { CloseThinIcon } from '@starrez/starryui';
import { ReactPortal } from '@starryui10/layouts/ReactPortal';
import * as React from 'react';
import { v1 as uuid } from 'uuid';
import { Image } from './components/Image';
import { Text } from '@starryui10/typography/Text';
import { useState } from 'react';
import { Center } from '@starryui10/layouts/Center';
import { VStack, HStack } from '@starryui10/layouts/Stack';
import { EmptyState } from '@starryui10/Components/EmptyState';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { strings } from '@starryui10/localisation';
import { IconButton } from '@starryui10/Buttons/IconButton';

interface IPhotoViewerDialogProps extends photoViewer.PhotoViewerProps {
    onClose: () => void;
    src: string;
    emptyState?: React.FC;
    minHeight?: number;
    minWidth?: number;
}

interface IState {
    width?: number;
    height?: number;
}

export const MODAL_HEADER_HEIGHT = 48;

export const PhotoViewerDialog = ({
    minHeight = 336,
    minWidth = 336,
    altText,
    imgRole = 'presentation',
    src,
    popupHeader,
    onClose,
    emptyState: EmptyStateComponent,
}: IPhotoViewerDialogProps) => {
    const closeButtonRef = React.createRef<HTMLButtonElement>();
    const idToUse = uuid();

    const [state, setState] = React.useState<IState>({});

    const [display, setDisplay] = useState<'EmptyState' | 'Image' | 'Loading'>('Loading');

    React.useEffect(() => {
        if (closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
    }, []);

    React.useEffect(() => {
        setDisplay('Loading');
    }, [src]);

    const stopClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    };

    const handleCloseAction = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        e.stopPropagation();
        if (e.nativeEvent.code === 'Escape') {
            onClose();
        }
    };

    const handleOnError = () => {
        setDisplay('EmptyState');

        setState({
            height: minHeight,
            width: minWidth,
        });
    };

    const handleOnImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const target = e.target as HTMLImageElement;
        setDisplay('Image');

        setState({
            height: target.naturalHeight + MODAL_HEADER_HEIGHT,
            width: target.naturalWidth,
        });
    };

    return (
        <ReactPortal>
            <ModalDiv onClick={handleCloseAction}>
                <MotionSection
                    animate={display === 'Image' ? state : {}}
                    transition={{ ease: 'linear' }}
                    role="dialog"
                    aria-labelledby={idToUse}
                    onClick={stopClick}
                    onKeyDown={handleKeyDown}
                    tabIndex={0}
                    minHeight={`${minHeight}px`}
                    minWidth={`${minWidth}px`}
                >
                    <VStack height="100%" rows={`${MODAL_HEADER_HEIGHT}px auto`}>
                        <HStack pad="xSmall" columns="1fr auto">
                            <Text id={idToUse} renderAs="h3" variant="large.bold">
                                {popupHeader}
                            </Text>

                            <IconButton title={strings.Action_ClosePopup} ariaLabel={strings.Action_ClosePopup} onClick={handleCloseAction}>
                                <CloseThinIcon Height="24px" Width="24px" />
                            </IconButton>
                        </HStack>

                        <ContentContainerDiv>
                            {display === 'EmptyState' && (
                                <HStack pad="small">
                                    {EmptyStateComponent ? (
                                        <Center>
                                            <EmptyStateComponent />
                                        </Center>
                                    ) : (
                                        <Center>
                                            <EmptyState
                                                title="Image not found"
                                                description="The image could not be loaded"
                                                variant="Large"
                                                graphic="Document"
                                            />
                                        </Center>
                                    )}
                                </HStack>
                            )}

                            {display === 'Loading' && (
                                <Center>
                                    <Text>Loading...</Text>
                                </Center>
                            )}

                            <Image
                                altText={altText}
                                onLoad={handleOnImageLoad}
                                onError={handleOnError}
                                role={imgRole}
                                src={src}
                                showImage={display === 'Image'}
                            />
                        </ContentContainerDiv>
                    </VStack>
                </MotionSection>
            </ModalDiv>
        </ReactPortal>
    );
};

const ContentContainerDiv = styled.div`
    border-radius: 0px 0px 6px 6px;
    overflow: hidden;
    height: 100%;
`;

const ModalDiv = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(50, 55, 61, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
`;

const MotionSection = styled(motion.section)<{ minHeight: string; minWidth: string }>`
    min-height: ${(props) => props.minHeight};
    min-width: ${(props) => props.minWidth};
    max-width: 100vw;
    max-height: 100vh;
    position: relative;
    background-color: white;
    display: flex;
    flex-direction: column;
    border-radius: 6px;
`;
